import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { QrCode, Download, ExternalLink, Users, UserPlus, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QRCodes = () => {
  const { agent, token } = useAuth();
  const [qrCodeData, setQrCodeData] = useState(null);
  const [privateLeadQrData, setPrivateLeadQrData] = useState(null);
  const [reviewQrData, setReviewQrData] = useState(null);

  useEffect(() => {
    if (agent) {
      fetchQRCode();
      fetchPrivateLeadQR();
      fetchReviewQR();
    }
  }, [agent]);

  const fetchQRCode = async () => {
    try {
      const response = await axios.get(`${API}/agent/service-provider-qr`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCodeData(response.data);
    } catch (error) {
      console.error('Failed to fetch QR code:', error);
    }
  };

  const fetchPrivateLeadQR = async () => {
    try {
      const response = await axios.get(`${API}/agent/private-lead-qr`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrivateLeadQrData(response.data);
    } catch (error) {
      console.error('Failed to fetch private lead QR:', error);
    }
  };

  const fetchReviewQR = async () => {
    try {
      const userType = agent?.role || 'agent';
      const userId = agent?.id;
      if (userId) {
        const response = await axios.get(`${API}/reviews/qr/${userType}/${userId}`);
        setReviewQrData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch review QR:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const downloadQRCode = (containerId, filename) => {
    const svg = document.getElementById(containerId)?.querySelector('svg');
    if (!svg) return toast.error('QR code not found');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 600, 600);
      ctx.drawImage(img, 0, 0, 600, 600);
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR Code downloaded!');
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="qr-codes-page">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>QR Codes</h1>
          <p className="text-stone-600">All your QR codes in one place — share, download, or print</p>
        </div>

        <div className="space-y-6">
          {/* Service Provider Registration QR */}
          {qrCodeData && (
            <Card className="bg-white p-6 rounded-2xl card-shadow" data-testid="qr-service-provider">
              <div className="flex items-start gap-3 mb-5">
                <div className="p-2.5 bg-[#1A4D2E]/10 rounded-xl">
                  <UserPlus size={22} className="text-[#1A4D2E]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1A4D2E]">Service Provider Registration</h3>
                  <p className="text-sm text-stone-600 mt-0.5">
                    Share with service providers (Handyman, Attorney, etc.) to connect them to your network
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
                <div id="qr-sp-container" className="bg-white p-4 rounded-lg border-2 border-stone-200 flex-shrink-0">
                  <QRCode value={qrCodeData.registration_url} size={200} level="H" />
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-stone-800 mb-2">How to use:</h4>
                  <ol className="list-decimal list-inside space-y-1.5 text-sm text-stone-600">
                    <li>Show this QR code to service providers</li>
                    <li>They scan it with their phone camera</li>
                    <li>They fill out their information</li>
                    <li>Their details are saved to your network</li>
                  </ol>

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>Registration URL:</strong>
                      <br />
                      <span className="font-mono text-xs break-all">{qrCodeData.registration_url}</span>
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      onClick={() => downloadQRCode('qr-sp-container', 'service-provider-registration-qr')}
                      size="sm"
                      className="bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                      data-testid="download-sp-qr"
                    >
                      <Download size={14} className="mr-1.5" />
                      Download QR Code
                    </Button>
                    <Button
                      onClick={() => copyToClipboard(qrCodeData.registration_url)}
                      variant="outline"
                      size="sm"
                      data-testid="copy-sp-link"
                    >
                      Copy Link
                    </Button>
                    <Button
                      onClick={() => window.open('/service-providers', '_blank')}
                      size="sm"
                      variant="outline"
                    >
                      <ExternalLink size={14} className="mr-1.5" />
                      View My Providers
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Private Lead Sign-In QR */}
          {privateLeadQrData && (
            <Card className="bg-white p-6 rounded-2xl card-shadow" data-testid="qr-private-lead">
              <div className="flex items-start gap-3 mb-5">
                <div className="p-2.5 bg-[#1A4D2E]/10 rounded-xl">
                  <Users size={22} className="text-[#1A4D2E]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1A4D2E]">Private Lead Sign-In</h3>
                  <p className="text-sm text-stone-600 mt-0.5">
                    Collect leads directly without an open house — perfect for walk-ins, referrals, or personal meetings
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
                <div id="qr-pl-container" className="bg-white p-4 rounded-lg border-2 border-stone-200 flex-shrink-0">
                  <QRCode value={privateLeadQrData.signin_url} size={200} level="H" />
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-stone-800 mb-2">How to use:</h4>
                  <ol className="list-decimal list-inside space-y-1.5 text-sm text-stone-600">
                    <li>Show this QR code to potential clients</li>
                    <li>They scan it with their phone camera</li>
                    <li>They fill out their contact information</li>
                    <li>Lead appears in your Leads page (marked as "Private Lead")</li>
                    <li>You can assign them to service providers as needed</li>
                  </ol>

                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-xs text-emerald-800">
                      <strong>Privacy:</strong> These leads are only visible to you. They won't appear in your broker's view unless you explicitly share them.
                    </p>
                  </div>

                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>Sign-In URL:</strong>
                      <br />
                      <span className="font-mono text-xs break-all">{privateLeadQrData.signin_url}</span>
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      onClick={() => downloadQRCode('qr-pl-container', 'private-lead-signin-qr')}
                      size="sm"
                      className="bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                      data-testid="download-pl-qr"
                    >
                      <Download size={14} className="mr-1.5" />
                      Download QR Code
                    </Button>
                    <Button
                      onClick={() => copyToClipboard(privateLeadQrData.signin_url)}
                      variant="outline"
                      size="sm"
                      data-testid="copy-pl-link"
                    >
                      Copy Link
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Customer Reviews QR */}
          {reviewQrData && (
            <Card className="bg-white p-6 rounded-2xl card-shadow" data-testid="qr-reviews">
              <div className="flex items-start gap-3 mb-5">
                <div className="p-2.5 bg-[#1A4D2E]/10 rounded-xl">
                  <Star size={22} className="text-[#1A4D2E]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1A4D2E]">Customer Reviews</h3>
                  <p className="text-sm text-stone-600 mt-0.5">
                    Share with your customers so they can leave reviews about your services
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
                <div id="qr-review-container" className="bg-white p-4 rounded-lg border-2 border-stone-200 flex-shrink-0">
                  {reviewQrData.qr_code ? (
                    <img src={reviewQrData.qr_code} alt="Review QR Code" className="w-[200px] h-[200px]" />
                  ) : (
                    <QRCode value={reviewQrData.review_url} size={200} level="H" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-stone-800 mb-2">How to use:</h4>
                  <ol className="list-decimal list-inside space-y-1.5 text-sm text-stone-600">
                    <li>Share this QR code with your customers after providing service</li>
                    <li>They scan it with their phone camera to leave a review</li>
                    <li>Or copy and share the review link directly via email or text</li>
                    <li>Download and print the QR code to display at your office</li>
                  </ol>

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>Review URL:</strong>
                      <br />
                      <span className="font-mono text-xs break-all">{reviewQrData.review_url}</span>
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      onClick={() => {
                        if (reviewQrData.qr_code) {
                          const link = document.createElement('a');
                          link.href = reviewQrData.qr_code;
                          link.download = `review-qr-code-${agent?.role}-${agent?.id}.png`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          toast.success('QR Code downloaded!');
                        } else {
                          downloadQRCode('qr-review-container', `review-qr-code-${agent?.role}-${agent?.id}`);
                        }
                      }}
                      size="sm"
                      className="bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                      data-testid="download-review-qr"
                    >
                      <Download size={14} className="mr-1.5" />
                      Download QR Code
                    </Button>
                    <Button
                      onClick={() => copyToClipboard(reviewQrData.review_url)}
                      variant="outline"
                      size="sm"
                      data-testid="copy-review-link"
                    >
                      Copy Link
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Empty state */}
          {!qrCodeData && !privateLeadQrData && !reviewQrData && (
            <Card className="bg-white p-12 rounded-2xl card-shadow text-center">
              <QrCode size={48} className="mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500">Loading your QR codes...</p>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default QRCodes;
